'use client'

import React, { useCallback, useEffect, useId, useState } from 'react'
import { WagamourV2Modal } from '@/components/v2/WagamourV2Modal'
import { WagamourV2Button } from '@/components/v2/WagamourV2Button'
import {
  WagamourV2ModalEyebrow,
  WagamourV2ModalStoryLine,
} from '@/components/v2/WagamourV2ModalContent'
import { PlayCircleOrbitAvatar } from '@/components/play/PlayCircleOrbitAvatar'
import { v2ModalStyles } from '@/lib/design/v2ModalContent'
import type { CirclePlaydateDto } from '@/lib/play/circlePlaydateDto'
import { formatCirclePlaydateWhenLabel } from '@/lib/play/formatCirclePlaydateWhen'
import { trackPlayV2Event } from '@/lib/play/playV2Analytics'

export type CirclePlaydateProposalReviewSheetProps = {
  isOpen: boolean
  playdate: CirclePlaydateDto | null
  activeDog: { name: string; photoUrl?: string | null }
  onClose: () => void
  /** Fired after successful accept/decline — parent should open relationship. */
  onResolved: (playdate: CirclePlaydateDto, outcome: 'accepted' | 'declined') => void
  portalContainer?: HTMLElement | null
}

type Phase = 'review' | 'confirm-accept' | 'confirm-decline'

function formatWhen(playdate: CirclePlaydateDto): string {
  return formatCirclePlaydateWhenLabel({
    scheduledStartAt: playdate.scheduledStartAt,
    timezone: playdate.timezone,
    presentationMode: playdate.presentationMode,
  })
}

export function CirclePlaydateProposalReviewSheet({
  isOpen,
  playdate,
  activeDog,
  onClose,
  onResolved,
  portalContainer = null,
}: CirclePlaydateProposalReviewSheetProps) {
  const titleId = useId()
  const [phase, setPhase] = useState<Phase>('review')
  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) {
      setPhase('review')
      setBusy(null)
      setError(null)
    }
  }, [isOpen])

  const handleAction = useCallback(
    async (action: 'accept' | 'decline') => {
      if (!playdate) return
      trackPlayV2Event(
        action === 'accept' ? 'playdate_accept_tapped' : 'playdate_decline_tapped',
        {
          playdateId: playdate.id,
          relationshipId: playdate.relationshipId,
          activeDogId: playdate.inviteeDog.id,
          source: 'relationship',
        }
      )
      setBusy(action)
      setError(null)
      try {
        const res = await fetch(`/api/play/playdates/${playdate.id}/${action}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dogId: playdate.inviteeDog.id }),
        })
        const json = (await res.json().catch(() => ({}))) as {
          success?: boolean
          error?: string
          code?: string
          playdate?: CirclePlaydateDto
        }
        if (!res.ok || !json.success || !json.playdate) {
          setError(
            json.code === 'PLAYDATE_EXPIRED'
              ? 'This invitation has expired.'
              : json.error ?? 'Something went wrong. Try again.'
          )
          setPhase('review')
          return
        }
        onResolved(json.playdate, action === 'accept' ? 'accepted' : 'declined')
      } catch {
        setError('Network error — try again.')
        setPhase('review')
      } finally {
        setBusy(null)
      }
    },
    [onResolved, playdate]
  )

  if (!playdate) return null

  const friendName = playdate.initiatorDog.name.trim() || 'Friend'
  const where = playdate.parkName || playdate.customLocationLabel || 'Public place'
  const when = formatWhen(playdate)

  return (
    <WagamourV2Modal
      isOpen={isOpen}
      onClose={() => {
        if (busy) return
        onClose()
      }}
      presentation="sheet"
      portalContainer={portalContainer}
      preventClose={busy !== null}
      ariaLabelledBy={titleId}
      header={
        <div className="space-y-1">
          <WagamourV2ModalEyebrow>Playdate invitation</WagamourV2ModalEyebrow>
          <h2 id={titleId} className="text-base font-bold leading-snug" style={v2ModalStyles.headline}>
            {phase === 'confirm-accept'
              ? 'Accept playdate?'
              : phase === 'confirm-decline'
                ? 'Decline invitation?'
                : `${friendName} invited ${activeDog.name.trim() || 'your dog'}`}
          </h2>
        </div>
      }
      footer={
        <div className="space-y-2">
          {phase === 'review' ? (
            <>
              <WagamourV2Button
                type="button"
                fullWidth
                disabled={busy !== null}
                onClick={() => {
                  setError(null)
                  setPhase('confirm-accept')
                }}
                data-playdate-accept="true"
              >
                Accept
              </WagamourV2Button>
              <WagamourV2Button
                type="button"
                fullWidth
                variant="secondary"
                disabled={busy !== null}
                onClick={() => {
                  setError(null)
                  setPhase('confirm-decline')
                }}
                data-playdate-decline="true"
              >
                Decline
              </WagamourV2Button>
            </>
          ) : phase === 'confirm-accept' ? (
            <>
              <WagamourV2Button
                type="button"
                fullWidth
                disabled={busy !== null}
                onClick={() => void handleAction('accept')}
                data-playdate-confirm-accept="true"
              >
                {busy === 'accept' ? 'Accepting…' : 'Yes, accept'}
              </WagamourV2Button>
              <WagamourV2Button
                type="button"
                fullWidth
                variant="secondary"
                disabled={busy !== null}
                onClick={() => setPhase('review')}
              >
                Back
              </WagamourV2Button>
            </>
          ) : (
            <>
              <WagamourV2Button
                type="button"
                fullWidth
                variant="secondary"
                disabled={busy !== null}
                onClick={() => void handleAction('decline')}
                data-playdate-confirm-decline="true"
              >
                {busy === 'decline' ? 'Declining…' : 'Yes, decline'}
              </WagamourV2Button>
              <WagamourV2Button
                type="button"
                fullWidth
                disabled={busy !== null}
                onClick={() => setPhase('review')}
              >
                Keep reviewing
              </WagamourV2Button>
            </>
          )}
          {error ? (
            <p className="text-center text-xs text-rose-200" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      }
    >
      <div className="space-y-4" aria-labelledby={titleId} data-playdate-proposal-review="true">
        {phase === 'confirm-accept' ? (
          <p className="text-center text-sm leading-relaxed" style={v2ModalStyles.body}>
            You&apos;ll meet {friendName} · {when} · {where}.
          </p>
        ) : phase === 'confirm-decline' ? (
          <p className="text-center text-sm leading-relaxed" style={v2ModalStyles.body}>
            {friendName} will be notified. You stay friends in your Play Circle.
          </p>
        ) : (
          <>
            <div className="flex items-center justify-center gap-3">
              <PlayCircleOrbitAvatar
                name={friendName}
                photoUrl={playdate.initiatorDog.photoUrl}
                size={52}
                variant="friend"
              />
              <PlayCircleOrbitAvatar
                name={activeDog.name}
                photoUrl={activeDog.photoUrl}
                size={52}
                variant="center"
              />
            </div>
            <div className="space-y-3 rounded-[20px] border p-4" style={v2ModalStyles.nestedCard}>
              <WagamourV2ModalStoryLine label="When">{when}</WagamourV2ModalStoryLine>
              <WagamourV2ModalStoryLine label="Where">{where}</WagamourV2ModalStoryLine>
            </div>
          </>
        )}
      </div>
    </WagamourV2Modal>
  )
}
